import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useMeasure from "react-use-measure";
import { ParagraphButtonData, SimpleScenario, SimpleScene, ButtomMenuButton } from "@/model";
import { isValidParagraphUrl, isVariableFormat } from "@/util/helper/paragraphHelper";
import { useScenariosAll } from "@/query/scenario";
import { useSimpleScenes } from "@/query/scene";
import { useScenarioId } from "@/hook/useScenarioId";
import { TextInput, Typeahead, TypeaheadInput, TypeaheadOptions } from "@/component/input";

import { SceneTypeahead } from ".";


const BREAKPOINT_WIDTH = 150;

interface ButtonInputsFactoryProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    disabled?: boolean;
    onChange: (button: T) => void;
}

const ButtonInputsFactory = <T extends ParagraphButtonData | ButtomMenuButton>(props: ButtonInputsFactoryProps<T>) => {
    const { button, disabled = false, onChange } = props;

    const scenarioId = useScenarioId();

    const { data: scenarios } = useScenariosAll();
    const { data: scenes } = useSimpleScenes(scenarioId);

    switch (button.button_type) {
        case "text":
            return (
                <TextInputs button={button}
                            disabled={disabled}
                            onChange={onChange}
                />
            );
        case "next":
            if (!scenes) {
                return null;
            }
            return (
                <NextInputs button={button}
                            scenes={scenes}
                            disabled={disabled}
                            onChange={onChange}
                />
            );
        case "link":
            return (
                <LinkInputs button={button}
                            disabled={disabled}
                            onChange={onChange}
                />
            );
        case "scenario":
            if (!scenarios) {
                return null;
            }
            return (
                <ScenarioInputs button={button}
                                scenarios={scenarios}
                                disabled={disabled}
                                onChange={onChange}
                />
            );
        case "intent":
            return (
                <IntentInputs button={button}
                              disabled={disabled}
                              onChange={onChange}
                />
            );
        case "back":
        default:
            return null;
    }
};

interface TextInputsProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    disabled?: boolean;
    onChange: (data: T) => void;
}

const TextInputs = <T extends ParagraphButtonData | ButtomMenuButton>(props: TextInputsProps<T>) => {
    const { button, disabled = false, onChange } = props;

    const intl = useIntl();
    const [measureRef, { width: inputWidth }] = useMeasure();

    const editablePayload = useMemo(() => {
        const payload = button.payload ?? "";
        if (!payload) {
            return "";
        }
        if (payload.startsWith("text:")) {
            return payload.replace("text:", "");
        }
        if (payload.startsWith("{") && payload.includes("text:")) {
            return payload;
        }
        return "";
    }, [button.payload]);

    return (
        <TextInput ref={measureRef}
                   required
                   disabled={disabled}
                   value={editablePayload}
                   placeholder={intl.formatMessage({ id: inputWidth < BREAKPOINT_WIDTH ? "i04013_1" : "i04013" })}
                   onChange={e => {
                       const value = e.target.value;
                       onChange({
                           ...button,
                           payload: (value.startsWith("{") && value.includes("text:")) ? value : `text:${value}`
                       });
                   }}
        />
    );
};

interface NextInputsProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    scenes: SimpleScene[];
    disabled?: boolean;
    onChange: (data: T) => void;
}

const NextInputs = <T extends ParagraphButtonData | ButtomMenuButton>(props: NextInputsProps<T>) => {
    const { button, scenes, disabled = false, onChange } = props;

    const buttonNext = useMemo(() => {
        if (button.next) {
            return button.next;
        }
        const payload = button.payload ?? "";
        if (!payload) {
            return "";
        }
        if (isVariableFormat(payload)) {
            return payload;
        }
        if (payload.startsWith("custom_id:")) {
            return payload.replace("custom_id:", "");
        }
        return "";
    }, [button.next, button.payload]);

    const onChangeNext = (customId: string) => {
        onChange({
            ...button,
            next: customId,
            payload: `custom_id:${customId}`
        });
    };

    return (
        <SceneTypeahead scenes={scenes ?? []}
                        disabled={disabled}
                        customId={buttonNext}
                        onChange={onChangeNext}
        />
    );
};

interface LinkInputsProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    disabled?: boolean;
    onChange: (data: T) => void;
}

const LinkInputs = <T extends ParagraphButtonData | ButtomMenuButton>(props: LinkInputsProps<T>) => {
    const { button, disabled = false, onChange } = props;

    const intl = useIntl();
    const [mobileInputMeasureRef, { width: mobileInputWidth }] = useMeasure();
    const [pcInputMeasureRef, { width: pcInputWidth }] = useMeasure();

    return (
        <div className="w-full flex flex-col gap-1">
            <TextInput ref={mobileInputMeasureRef}
                       required
                       invalid={!isValidParagraphUrl(button.link_mobile ?? "")}
                       disabled={disabled}
                       value={button.link_mobile ?? ""}
                       placeholder={intl.formatMessage({ id: mobileInputWidth < BREAKPOINT_WIDTH ? "i04019_1" : "i04019" })}
                       onChange={e => onChange({
                           ...button,
                           link_mobile: e.target.value
                       })}
            />
            <TextInput ref={pcInputMeasureRef}
                       invalid={!!button.link_pc && !isValidParagraphUrl(button.link_pc ?? "")}
                       disabled={disabled}
                       value={button.link_pc ?? ""}
                       placeholder={intl.formatMessage({ id: pcInputWidth < BREAKPOINT_WIDTH ? "i04020_1" : "i04020" })}
                       onChange={e => onChange({
                           ...button,
                           link_pc: e.target.value
                       })}
            />
        </div>
    );
};

const scenarioLabel = (scenario?: SimpleScenario) => scenario ? `${scenario.id}: ${scenario.name}` : "";
const findScenario = (scenarios: SimpleScenario[], keyword?: string) => scenarios.find(item => item.id === keyword || scenarioLabel(item) === keyword);

interface ScenarioInputsProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    scenarios: SimpleScenario[];
    disabled?: boolean;
    onChange: (data: T) => void;
}

const ScenarioInputs = <T extends ParagraphButtonData | ButtomMenuButton>(props: ScenarioInputsProps<T>) => {
    const { button, scenarios, disabled = false, onChange } = props;

    const payloadArray = useMemo(() => {
        const payloadArray = button.payload?.split(":") ?? [];
        if (payloadArray[0] !== "act_scenario" || payloadArray.length < 3) {
            return ["", ""];
        }
        return payloadArray.splice(1,2);
    }, [button.payload]);
    const selectedScenario = useMemo(() => findScenario(scenarios, payloadArray[0]), [scenarios, payloadArray]);

    const intl = useIntl();
    const [scenarioTypeaheadMeasureRef, { width: scenarioTypeaheadWidth }] = useMeasure();
    const [queryInputMeasureRef, { width: queryInputWidth }] = useMeasure();

    const [searchKeyword, setSearchKeyword] = useState(scenarioLabel(selectedScenario) || "");

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value.includes("{{")) {
            onChange({
                ...button,
                payload: `act_scenario:${e.target.value}:${payloadArray[1]}`
            });
        } else {
            const selectedByInput = findScenario(scenarios, e.target.value);
            if (selectedByInput) {
                onChange({
                    ...button,
                    payload: `act_scenario:${selectedByInput.id.toString()}:${payloadArray[1]}`
                });
                setSearchKeyword(scenarioLabel(selectedByInput));
            } else {
                setSearchKeyword(scenarioLabel(selectedScenario) || payloadArray[0] || "");
            }
        }
    };

    const filteredScenarios = useMemo(() => {
        return scenarios.filter(scenario => scenarioLabel(scenario).includes(searchKeyword));
    }, [scenarios, searchKeyword]);

    return (
        <div className="w-full flex flex-col gap-1">
            <Typeahead ref={scenarioTypeaheadMeasureRef}
                       selection={selectedScenario && scenarioLabel(selectedScenario)}
                       onChange={itemName => {
                           const selected = scenarios.find(item => scenarioLabel(item) === itemName);
                           onChange({
                               ...button,
                               payload: `act_scenario:${selected?.id?.toString() ?? ""}:${payloadArray[1]}`
                           });
                           setSearchKeyword(itemName ?? "");
                       }}
            >
                <TypeaheadInput required
                                disabled={disabled}
                                value={searchKeyword}
                                placeholder={intl.formatMessage({ id: scenarioTypeaheadWidth < BREAKPOINT_WIDTH ? "i04026_1" : "i04026" })}
                                onChange={e => setSearchKeyword(e.target.value)}
                                onBlur={onBlur}
                />
                <TypeaheadOptions options={filteredScenarios.map(scenarioLabel)} />
            </Typeahead>
            <input ref={queryInputMeasureRef}
                   type="text"
                   className="input-default"
                   disabled={disabled}
                   value={payloadArray[1] ?? ""}
                   placeholder={intl.formatMessage({ id: queryInputWidth < BREAKPOINT_WIDTH ? "i04022_1" : "i04022" })}
                   onChange={e => onChange({
                       ...button,
                       payload: `act_scenario:${payloadArray[0]}:${e.target.value}`
                   })}
            />
        </div>
    );
};

interface IntentInputsProps<T extends ParagraphButtonData | ButtomMenuButton> {
    button: T;
    disabled?: boolean;
    onChange: (data: T) => void;
}

const IntentInputs = <T extends ParagraphButtonData | ButtomMenuButton>(props: IntentInputsProps<T>) => {
    const { button, disabled = false, onChange } = props;

    const editablePayload = useMemo(() => {
        const payload = button.payload;
        if (payload?.startsWith("intent:")) {
            return payload.replace("intent:", "");
        }
        return "";
    }, [button.payload]);

    return (
        <TextInput required
                   disabled={disabled}
                   value={editablePayload}
                   onChange={e => onChange({ ...button, payload: `intent:${e.target.value}` })}
        />
    );
};

export default ButtonInputsFactory;
